/**
 * Masternode Details for the Nexus application.
 *
 * <i>Copyright (c) 2020 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/masternodes/details.jsx
 * @class SubPageMasterNodes
 * @since 0.3.8
*/
import React, {useState, useEffect} from 'react';
import Web3 from 'web3';
import checkPortStatus from 'itsa-browser-portscan';
import {delay} from 'itsa-utils';
import {useStore} from 'effector-react';
import {DataTable, DataTableContent, DataTableBody, DataTableRow, DataTableCell} from '@rmwc/data-table';
import {Icon} from '@rmwc/icon';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import {IconButton} from '@rmwc/icon-button';
import {CircularProgress} from '@rmwc/circular-progress';
import {Tooltip} from '@rmwc/tooltip';
import Footer from '../../Footer';
import blockPolling from '../../../globals/block-polling';
import {showIP} from '../../../lib/helpers/enode-ip';
import formatNumber from '../../../lib/helpers/bn-format';
import imgContentCopy24 from '../../../images/content_copy_24.png';
import copyToClipboard from '../../../lib/helpers/clipboard';
import translations from '../../../lib/translations';
import {statusTextToI18nKey} from '../../../lib/helpers/masternode-status-text';
import menuState from '../../../globals/menu';
import {networkId} from '../../../lib/helpers/network-providers';
import openFlag from '../../../images/open_flag.svg';
import closeFlag from '../../../images/close_flag.svg';
import masternodeListing from '../../../globals/masternode-listing';

const TIMEOUT_PORTSCAN = 15000,
    toBN = Web3.utils.toBN,
    THOUSAND_WEI_BN = toBN('1000000000000000000000'),
    CLASS_VERSION_ICON = {
        latest: <Icon className="version-status latest" icon="check_circle" />,
        outdated: <Icon className="version-status outdated" icon="warning" />,
        critical: <Icon className="version-status critical" icon="report" />
    };

const MasternodeDetails = props => {
    const data = props.data,
        allMNdata = useStore(masternodeListing.$data),
        currentPayout = useStore(masternodeListing.$currentPayout),
        lastBlock = useStore(blockPolling.$lastBlock),
        [snackbarMsg, setSnackbarMsg] = useState(''),
        [rowCollapse, setRowCollapse] = useState(false),
        [portscanSupport, setPortscanSupport] = useState(false),
        [isPortOpen, setIsPortOpen] = useState(''),
        locales = props.langInfo && props.langInfo.locales,
        translate = translations.init(props);
    let flagsWrapper, notificationWrapper, portRow, mount = true, versionStatus, nextPayoutMessage;

    // A: find the index of the selected MN using it's owner address

    const mnIndex = allMNdata.findIndex(e => e.owner === data.owner);

    // B: calculate the sum of all masternode collaterals asides that of the selected MN
    const collateralSum = allMNdata.filter(({isActive}, index) => isActive && (index < mnIndex))
        .reduce((sum, obj) => sum.add(obj.collateral), toBN('0'));

    // D: Find amount paid to currently rewarded masternode with the percentage calculated
    const amountPaidToRewardedMN = currentPayout * 1000;
    const amountPaidToRewardedMnBN = toBN(Web3.utils.toWei(amountPaidToRewardedMN.toString(), 'ether'));

    // E: deduct amount paid already to first MN from sum calculated in comment (B)
    const amountLeft = collateralSum.sub(amountPaidToRewardedMnBN);

    // F: divide by 1000, as portions and divide portions by 10 to get how many blocks required before MN is rewarded
    const portionBN = amountLeft.div(THOUSAND_WEI_BN);
    const portions = parseInt(portionBN.toString(), 10);
    const numberOfBlocks = Math.ceil(portions / 10);

    if (allMNdata[0].owner === data.owner) {
        nextPayoutMessage = `${translate.t('MOMENTARILY', true)}`;
    }
    else {
        nextPayoutMessage = `${numberOfBlocks} ${translate.t('BLOCKS', true)}`;
    }

    useEffect(() => {
        checkIsOpenport(true);
        menuState.on.hide();
        window.scrollTo(0, 0);

        return () => {
            mount = false;
            menuState.on.open();
        };
    }, []);


    const reducer = (cum, item) => {
        const itemVersion = item.sWVersion.split('.');
        if (itemVersion[0] > cum.x) {
            return {x:itemVersion[0], y:itemVersion[1], z:itemVersion[2]};
        }
        else if (cum.x < itemVersion[0]) {
            return cum;
        }
        // now, cumVersion.x === itemVersion.x
        if (itemVersion[1] > cum.y) {
            return {x:itemVersion[0], y:itemVersion[1], z:itemVersion[2]};
        }
        else if (cum.y < itemVersion[1]) {
            return cum;
        }
        // now, cumVersion.x.y === itemVersion.x.y
        if (itemVersion[2] > cum.z) {
            return {x:itemVersion[0], y:itemVersion[1], z:itemVersion[2]};
        }
        return cum;
    };

    const getVersionStatus = (item, currVersion) => {
        let versionState, itemVersion = item.split('.');
        itemVersion = {x:itemVersion[0], y:itemVersion[1], z:itemVersion[2]};

        if (itemVersion.x < currVersion.x || itemVersion.y < currVersion.y) {
            versionState = 'critical';
        }
        else if (itemVersion.z<currVersion.z) {
            versionState = 'outdated';
        }
        else {
            versionState = 'latest';
        }
        return versionState;
    };

    if (allMNdata.length > 0) {
        const currentVersion = allMNdata.reduce(reducer, {x:0, y:0, z:0});
        versionStatus = getVersionStatus(data.sWVersion, currentVersion);
    }

    const checkIsOpenport = async init => {
        let isOpen, deviceSupportsPortscan;
        const enode = data.enode,
            host = showIP(enode),
            portNumber = networkId;
        try {
            if (init) {
                deviceSupportsPortscan = await props.deviceSupportsPortscan;
                if (!deviceSupportsPortscan) {
                    return;
                }
                setPortscanSupport(true);
            }
            isOpen = await checkPortStatus(host, portNumber, true, TIMEOUT_PORTSCAN);
            if (mount) {
                setIsPortOpen(isOpen);
                await delay(1000);
                checkIsOpenport();
            }
        }
        catch (err) {
            console.error(err);
        }
    };

    const copyClipboard = (text, what) => {
        if (copyToClipboard(text)) {
            // do not translate <what< -> these are Strings like public address
            setSnackbarMsg(what + ' ' + translate.t('COPIED_CLIPBOARD'));
        }
        else {
            setSnackbarMsg(translate.t('ACTION_NOT_SUPPORTED', true));
        }
    };

    if (portscanSupport) {
        if (isPortOpen !== '') {
            if (isPortOpen) {
                flagsWrapper = (
                    <DataTableCell alignStart><img alt="open flag" src={openFlag} />{translate.t('OPEN')}</DataTableCell>
                );
            }
            else {
                flagsWrapper = (
                    <DataTableCell alignStart><img alt="close flag" src={closeFlag} />{translate.t('CLOSED')}</DataTableCell>
                );
                notificationWrapper = (
                    <div className="port-warning-wrapper">
                        <div className="port-warning">
                            <p>{translate.t('CLOSED_MASTERNODE_SYNC_PORT', true)}</p>
                            <a href="https://wiki.energi.world/en/support/help-me" rel="noreferrer noopener" target="_blank">{translate.t('SUPPORT_WIKI', true)}</a>
                        </div>
                    </div>
                );
            }
        }
        else {
            flagsWrapper = (
                <DataTableCell alignStart><CircularProgress /></DataTableCell>
            );
        }
        portRow = (
            <DataTableRow>
                <DataTableCell>Ports</DataTableCell>
                {flagsWrapper}
            </DataTableRow>
        );
    }

    return (
        <div className="masternodes pure-u subpage no-menu">
            <section className="details">
                <div className="top-bar">
                    <table>
                        <tbody>
                            <tr>
                                <td><IconButton
                                    icon="arrow_back"
                                    onClick={() => props.setState(false)}
                                    style={{color: 'rgba(0, 0, 0, 0.54)'}}/>
                                </td>
                                <td><span className="top-bar-text">{translate.t('MASTERNODE_LIST_HEADER', true)}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {notificationWrapper}
                <div className="data-table">
                    <DataTable className="data-table-wrapper table-shadow">
                        <DataTableContent className="data-table-wrapper">
                            <DataTableBody>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('RANK', true)}</DataTableCell>
                                    <DataTableCell alignStart>{data.rank}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>IPv4</DataTableCell>
                                    <DataTableCell alignStart>{showIP(data.enode)}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('STATUS', true)}</DataTableCell>
                                    <DataTableCell alignStart className={data.isActive ? (data.isAlive ? 'is-active' : 'offline') : 'not-active'}>
                                        {translate.t(statusTextToI18nKey(data.isActive ? (data.isAlive ? 'active' : 'offline') : 'inactive'))}
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('VERSION', true)}</DataTableCell>
                                    <DataTableCell alignStart>{CLASS_VERSION_ICON[versionStatus]}<span className="version-text">{data.sWVersion}</span></DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('MASTERNODE', true)}</DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>{data.masternode}</td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_MASTERNODE_ADDRESS', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.masternode, 'Masternode')} />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('OWNER', true)}</DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>{data.owner}</td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_OWNER_ADDRESS', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.owner, translate.t('OWNER', true))} />
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>Enode</DataTableCell>
                                    <DataTableCell alignStart>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td><div className={rowCollapse ? 'collapse' : 'truncate'}>{data.enode}</div></td>
                                                    <td>
                                                        <Tooltip content={translate.t('COPY_ENODE', true)}>
                                                            <IconButton
                                                                className="img-fix"
                                                                icon={imgContentCopy24}
                                                                onClick={() => copyClipboard(data.enode, 'Enode')} />
                                                        </Tooltip>
                                                    </td>
                                                    <td className="td-end">
                                                        <IconButton
                                                            icon="keyboard_arrow_down"
                                                            onClick={() => setRowCollapse(!rowCollapse)}
                                                            onIcon="keyboard_arrow_up" />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('COLLATERAL', true)}</DataTableCell>
                                    <DataTableCell alignStart>{formatNumber(locales, data.collateral, 2)} NRG</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('NEXT_PAYOUT', true)}</DataTableCell>
                                    <DataTableCell alignStart>{nextPayoutMessage}</DataTableCell>
                                </DataTableRow>
                                <DataTableRow>
                                    <DataTableCell>{translate.t('AGE_MN', true)}</DataTableCell>
                                    <DataTableCell alignStart>{Math.max(0, lastBlock - data.announcedBlock)} {translate.t('BLOCKS', true)}</DataTableCell>
                                </DataTableRow>
                                {portRow}
                            </DataTableBody>
                        </DataTableContent>
                    </DataTable>
                </div>
            </section>
            <Snackbar
                action={<SnackbarAction icon="close" />}
                leading
                message={snackbarMsg}
                onClose={() => setSnackbarMsg('')}
                open={!!snackbarMsg}
                timeout={2020}/>
            <Footer {...props} />
        </div>
    );
};

export default MasternodeDetails;
